import { useState } from 'react'
import { FolderInput, Inbox } from 'lucide-react'
import { Button, ConfirmDialog, Toggle } from '@/components/ui'
import { EmptyState } from '@/components/common/EmptyState'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { SectionLoader } from '@/components/common/SectionLoader'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  useArchiveDocument,
  useDeleteCompanyDocument,
  useUnarchiveDocument,
  useUpdateCompanyDocumentExpiry,
} from '../../hooks/useDocuments'
import { useDriveFiles, useMoveDriveFiles } from '../../hooks/useDrive'
import type { DocumentGroup } from '../../utils/documentUtils'
import { ArchiveDocumentModal } from '../ArchiveDocumentModal'
import { EditExpiryModal } from '../EditExpiryModal'
import { GroupedDocumentList } from '../GroupedDocumentList'
import { DriveMoveDialog } from './DriveMoveDialog'

const PAGE_SIZE = 25

export interface DriveFilesProps {
  companyId: string
  /** A folder id or `unfiled`; left out to cover the whole company. */
  folderId?: string
  search?: string
  canManage: boolean
  canDelete: boolean
  emptyTitle: string
  emptyDescription?: string
  /** For search results; a folder's heading already says how many it holds. */
  showTotal?: boolean
  /** Library files can be moved; other people's files stay where they are. */
  canMove?: boolean
  /** Inside a folder, the folder already says what its files are. */
  hideCategory?: boolean
}

/** One page of files, with their actions. Give it a `key` per location to reset the page. */
export function DriveFiles({
  companyId,
  folderId,
  search,
  canManage,
  canDelete,
  emptyTitle,
  emptyDescription,
  showTotal = false,
  canMove = false,
  hideCategory = false,
}: DriveFilesProps): React.ReactElement {
  const [page, setPage] = useState(1)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [archiving, setArchiving] = useState<{ id: string; name: string } | null>(null)
  const [expiryGroup, setExpiryGroup] = useState<DocumentGroup | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [isMoving, setIsMoving] = useState(false)

  const { data, isLoading, error, refetch } = useDriveFiles(companyId, {
    folderId,
    search: search || undefined,
    includeArchived: includeArchived || undefined,
    page,
    limit: PAGE_SIZE,
  })
  const archive = useArchiveDocument(companyId)
  const unarchive = useUnarchiveDocument(companyId)
  const remove = useDeleteCompanyDocument(companyId)
  const updateExpiry = useUpdateCompanyDocumentExpiry(companyId)
  const move = useMoveDriveFiles(companyId)

  const files = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const pageIds = files.map((file) => file.id)
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))

  const setSelection = (ids: string[], isSelected: boolean): void => {
    setSelected((previous) => {
      const next = new Set(previous)
      for (const id of ids) {
        if (isSelected) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  return (
    <section className="space-y-3" aria-label="Files">
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-3">
          {canMove && files.length > 0 ? (
            <input
              type="checkbox"
              aria-label="Select all files on this page"
              checked={allOnPageSelected}
              onChange={(event) => setSelection(pageIds, event.target.checked)}
              className="ml-3 h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary"
            />
          ) : null}
          {selected.size > 0 ? (
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                {selected.size} selected
              </span>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setIsMoving(true)}
                className="gap-1.5 whitespace-nowrap"
              >
                <FolderInput className="h-3.5 w-3.5" aria-hidden />
                Move
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelected(new Set())}
                className="whitespace-nowrap"
              >
                Clear
              </Button>
            </div>
          ) : (
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Files
              {showTotal && total > 0 ? (
                <span className="ml-1.5 font-normal text-gray-500">{total}</span>
              ) : null}
            </h3>
          )}
        </div>
        <Toggle
          checked={includeArchived}
          onChange={(checked) => {
            setIncludeArchived(checked)
            setPage(1)
          }}
          label="Show archived"
        />
      </div>

      {isLoading ? (
        <SectionLoader variant="list" label="Loading files" />
      ) : error ? (
        <QueryErrorState error={error} size="inline" title="Couldn't load files" onRetry={refetch} />
      ) : files.length === 0 ? (
        <EmptyState icon={Inbox} title={emptyTitle} description={emptyDescription} />
      ) : (
        <>
          <GroupedDocumentList
            documents={files}
            bare
            hideCategory={hideCategory}
            canArchive={canManage}
            canDelete={canDelete}
            canEditExpiry={canManage}
            isArchivePending={archive.isPending}
            isUnarchivePending={unarchive.isPending}
            isDeletePending={remove.isPending}
            onArchive={(file) => setArchiving({ id: file.id, name: file.originalName })}
            onUnarchive={(id) => unarchive.mutate(id)}
            onDelete={setDeleteId}
            onEditExpiry={setExpiryGroup}
            selectedIds={canMove ? selected : undefined}
            onToggleSelected={canMove ? setSelection : undefined}
          />
          {total > PAGE_SIZE ? (
            <TablePaginationBar
              page={page}
              limit={PAGE_SIZE}
              total={total}
              totalPages={Math.ceil(total / PAGE_SIZE)}
              onPageChange={setPage}
            />
          ) : null}
        </>
      )}

      {isMoving ? (
        <DriveMoveDialog
          companyId={companyId}
          fileCount={selected.size}
          currentFolderId={folderId}
          isPending={move.isPending}
          onClose={() => setIsMoving(false)}
          onMove={(targetId) =>
            move.mutate(
              { fileIds: [...selected], folderId: targetId },
              {
                onSuccess: () => {
                  setIsMoving(false)
                  setSelected(new Set())
                },
              },
            )
          }
        />
      ) : null}
      <ArchiveDocumentModal
        isOpen={archiving !== null}
        onClose={() => setArchiving(null)}
        onConfirm={(archiveReason) => {
          if (!archiving) return
          archive.mutate(
            { id: archiving.id, archiveReason },
            { onSuccess: () => setArchiving(null) },
          )
        }}
        isPending={archive.isPending}
        documentName={archiving?.name}
      />
      <EditExpiryModal
        isOpen={expiryGroup !== null}
        onClose={() => setExpiryGroup(null)}
        onConfirm={(expiryDate) => {
          if (!expiryGroup) return
          updateExpiry.mutate(
            { id: expiryGroup.representativeDocument.id, expiryDate },
            { onSuccess: () => setExpiryGroup(null) },
          )
        }}
        isPending={updateExpiry.isPending}
        documentName={expiryGroup?.title}
        currentExpiry={expiryGroup?.expiryDate ?? null}
      />
      <ConfirmDialog
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (!deleteId) return
          remove.mutate(deleteId, { onSuccess: () => setDeleteId(null) })
        }}
        title="Delete file"
        message="Permanently delete this file? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={remove.isPending}
      />
    </section>
  )
}

DriveFiles.displayName = 'DriveFiles'
